import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { Product } from '../services/api';
import { getProductImageUrl } from '../utils/productImage';
import { Modal } from 'antd';
const { confirm } = Modal;

export interface CartItem {
  totalWeight: any;
  id: string;
  productId: string;
  variantId?: string;
  name: string;
  price: number;
  originalPrice?: number;
  initial_price?: number;
  fruits_price?: number;
  image: string;
  quantity: number;
  weight: string;
  unit: string;
  stock: number;
  type?: string;
  selected?: boolean;
  selectedItems?: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
    unit?: string;
    weight?: number;
  }>;
}

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  selectedItems: CartItem[];
  selectedSubtotal: number;
  selectedTotal: number;
  addItem: (product: any, quantity: number) => void;
  updateItemQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  toggleSelectItem: (id: string) => void;
  selectAllItems: () => void;
  unselectAllItems: () => void;
  isAllSelected: boolean;
  activePromotion: {
    code: string;
    discountAmount: number;
    discountType: string;
    discountValue: number;
  } | null;
  applyPromotion: (promotion: {
    code: string;
    type: string;
    value: number;
    constraints: {
      minimum_order_amount: number;
      maximum_discount_amount: number | null;
    };
  }) => void;
  removePromotion: () => void;
}

const STORAGE_KEY = 'fruitshop_cart';
const SESSION_KEY = 'fruitshop_cart_session';

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper function to save cart data to both localStorage and sessionStorage
const saveCartData = (items: CartItem[]) => {
  try {
    const cartData = JSON.stringify(items);
    localStorage.setItem(STORAGE_KEY, cartData);
    sessionStorage.setItem(SESSION_KEY, cartData);
    // console.log('Cart saved successfully', items);
  } catch (error) {
    console.error('Failed to save cart data:', error);
  }
};

// Helper function to load cart data from storage
const loadCartData = (): CartItem[] => {
  try {
    // First try to get from sessionStorage (for current session)
    const sessionData = sessionStorage.getItem(SESSION_KEY);
    if (sessionData) {
      return JSON.parse(sessionData);
    }

    // If not in session, try localStorage (for returning visitors)
    const localData = localStorage.getItem(STORAGE_KEY);
    if (localData) {
      // Also update sessionStorage for future use
      sessionStorage.setItem(SESSION_KEY, localData);
      return JSON.parse(localData);
    }
  } catch (error) {
    console.error('Failed to load cart data:', error);
    // Clear potentially corrupted data
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  }

  return [];
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [activePromotion, setActivePromotion] = useState<{
    code: string;
    discountAmount: number;
    discountType: string;
    discountValue: number;
  } | null>(null);

  // Load cart from storage on mount
  useEffect(() => {
    const savedCart = loadCartData();
    setItems(savedCart.map((item) => ({ ...item, selected: false })));
    setInitialized(true);
  }, []);

  // Save cart to storage whenever it changes
  useEffect(() => {
    if (initialized) {
      saveCartData(items);
    }
  }, [items, initialized]);

  // Calculate cart totals
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const shipping = subtotal >= 600000 ? 0 : 25000; // Free shipping over 600,000đ

  // Selected items calculations
  const selectedItems = items.filter((item) => item.selected);
  const selectedSubtotal = selectedItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const selectedShipping = selectedSubtotal >= 600000 ? 0 : 25000;
  const isAllSelected =
    items.length > 0 && items.every((item) => item.selected);

  // Calculate discount based on active promotion
  const discount = useMemo(() => {
    if (!activePromotion) return 0;

    const applicableAmount = selectedSubtotal;

    if (activePromotion.discountType === 'percentage') {
      // Calculate percentage discount
      const rawDiscount =
        applicableAmount * (activePromotion.discountValue / 100);
      // Check if there's a maximum discount amount
      if (
        activePromotion.discountAmount &&
        activePromotion.discountAmount > 0
      ) {
        return Math.min(rawDiscount, activePromotion.discountAmount);
      }
      return rawDiscount;
    } else {
      // Fixed amount discount
      return Math.min(activePromotion.discountValue, applicableAmount);
    }
  }, [activePromotion, selectedSubtotal]);

  const total = subtotal + shipping - discount;
  const selectedTotal = selectedSubtotal + selectedShipping - discount;

  // Toggle select/unselect an item
  const toggleSelectItem = (id: string) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  // Select all items
  const selectAllItems = () => {
    setItems((prevItems) =>
      prevItems.map((item) => ({ ...item, selected: true }))
    );
  };

  // Unselect all items
  const unselectAllItems = () => {
    setItems((prevItems) =>
      prevItems.map((item) => ({ ...item, selected: false }))
    );
  };

  // Add item to cart
  const addItem = (product: Product | any, quantity: number) => {
    if (quantity <= 0) return;

    setItems((prevItems) => {
      // Create a unique ID for each cart item using timestamp
      const uniqueCartId = `cart-${product.id}-${Date.now()}`;

      // Handle custom fruit box
      if (product.type === 'fruit_box') {
        // For custom fruit box, always add as new item
        return [
          ...prevItems,
          {
            id: uniqueCartId, // Unique cart item ID
            productId: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice || product.price,
            initial_price: product.initial_price,
            fruits_price: product.fruits_price,
            image: getProductImageUrl(product.image),
            quantity: quantity,
            weight: product.weight || '400g', // Default weight
            unit: product.unit || 'hộp',
            stock: product.stock || 99, // High stock for custom boxes
            type: 'fruit_box',
            selectedItems: product.selectedItems || [],
            totalWeight: product.totalWeight || product.weight || '400g',
            selected: false,
          },
        ];
      }

      // Handle multiple variants products
      if (product.type === 'multiple_variants') {
        return [
          ...prevItems,
          {
            id: uniqueCartId, // Unique cart item ID
            productId: product.productId,
            variantId: product.variantId, // Important for variants
            variantName: product.variantName, // Store variant name
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice,
            image: getProductImageUrl(product.images?.[0] || product.image),
            quantity: Math.min(quantity, product.stock || 99),
            weight: product.weight || '',
            unit: product.unit || '',
            stock: product.stock || 99,
            type: 'multiple_variants',
            totalWeight: product.totalWeight || product.weight || '',
            selected: false,
          },
        ];
      }

      // Handle regular single products
      return [
        ...prevItems,
        {
          id: uniqueCartId, // Unique cart item ID
          productId: product.productId,
          name: product.name,
          price: product.price,
          originalPrice: product.originalPrice,
          image: getProductImageUrl(product.images?.[0] || product.image),
          quantity: Math.min(quantity, product.stock || 99),
          weight: product.weight || '',
          unit: product.unit || '',
          stock: product.stock || 99,
          type: 'single',
          totalWeight: product.totalWeight || product.weight || '',
          selected: false,
        },
      ];
    });
  };

  // Update item quantity
  const updateItemQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }

    setItems((prevItems) => {
      return prevItems.map((item) => {
        if (item.id === id) {
          // Make sure we don't exceed stock
          return { ...item, quantity: Math.min(quantity, item.stock) };
        }
        return item;
      });
    });
  };

  // Remove item from cart
  const removeItem = (id: string) => {
    confirm({
      title: 'Bạn có chắc chắn muốn xóa sản phẩm này không?',
      onOk: () => {
        setItems((prevItems) => prevItems.filter((item) => item.id !== id));
      },
    });
  };

  // Clear cart
  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  };

  // Apply promotion to cart
  const applyPromotion = (promotion: {
    code: string;
    type: string;
    value: number;
    constraints: {
      minimum_order_amount: number;
      maximum_discount_amount: number | null;
    };
  }) => {
    // Check if selected subtotal meets minimum order amount
    if (selectedSubtotal < promotion.constraints.minimum_order_amount) {
      throw new Error(
        `Đơn hàng tối thiểu ${promotion.constraints.minimum_order_amount.toLocaleString(
          'vi-VN'
        )}₫`
      );
    }

    setActivePromotion({
      code: promotion.code,
      discountType: promotion.type,
      discountValue: promotion.value,
      discountAmount: promotion.constraints.maximum_discount_amount || 0,
    });
  };

  // Remove active promotion
  const removePromotion = () => {
    setActivePromotion(null);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        subtotal,
        shipping,
        discount,
        total,
        selectedItems,
        selectedSubtotal,
        selectedTotal,
        addItem,
        updateItemQuantity,
        removeItem,
        clearCart,
        toggleSelectItem,
        selectAllItems,
        unselectAllItems,
        isAllSelected,
        activePromotion,
        applyPromotion,
        removePromotion,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
